import { INFERRED_SOURCE_API_IDS } from '@/components/About/constants';
import { isRelease261Enabled } from '@/config/release261Features';
import { TAB_LABELS } from '../constants';

const getProperty = (foundTitle, properties) => {
  const foundProperty = properties.find(prop => foundTitle && foundTitle[prop]);
  return foundProperty ? foundTitle[foundProperty] : '';
};

export const getTitle = foundTitle => {
  return getProperty(foundTitle, ['name', 'symbol', 'strID', 'chemPrefName']);
};

export const getName = foundTitle => {
  return getProperty(foundTitle, ['diseaseUMLSCUI', 'description', 'geneSymbol', 'id']);
};

export const getId = foundTitle => {
  return getProperty(foundTitle, ['id', 'geneID', 'strID', 'id']);
};

export const getTabList = (view: string, tab: string) => {
  if (view === 'ALL') {
    return TAB_LABELS.filter(t =>
      ['GDA', 'VDA', 'DISEASES', 'GENES', 'VARIANTS', 'CHEMICALS'].includes(t.key),
    );
  } else if (view === 'GENES' && ['GDA', 'VDA', 'GENES'].includes(tab as string)) {
    return TAB_LABELS.filter(t => ['GDA', 'VDA', 'GENES'].includes(t.key));
  } else if (view === 'DISEASES' && ['GDA', 'VDA', 'DDA', 'DISEASES'].includes(tab as string)) {
    return TAB_LABELS.filter(t => ['GDA', 'VDA', 'DDA', 'DISEASES'].includes(t.key));
  } else if (view === 'VARIANTS' && ['VDA', 'VARIANTS'].includes(tab as string)) {
    return TAB_LABELS.filter(t => ['VDA', 'VARIANTS'].includes(t.key));
  } else if (view === 'CHEMICALS' && ['GDA', 'VDA', 'CHEMICALS'].includes(tab as string)) {
    return TAB_LABELS.filter(t => ['GDA', 'VDA', 'CHEMICALS'].includes(t.key));
  } else {
    console.error('Invalid tab selection');
    return [];
  }
};

const metricsRedirectNames = ['geneDSI', 'geneDPI', 'variantDSI', 'variantDPI', 'DSI', 'DPI', 'EI', 'score'];

const sourcesRedirectNames = ['jaccard_genes', 'jaccard_variants'];

const associationTypeRedirectNames = ['associationType'];

const attributesRedirectNames = [
  'geneProteinStrIDs',
  'proteinIDs',
  'EL',
  'firstRef',
  'yearInitial',
  'lastRef',
  'yearFinal',
  'pmid',
  'source',
  'DBSNPclass',
  'alleleFreqGenome',
  'genome',
  'alleleFreqExome',
  'exome',
  'coord',
  'alleles',
  'chromosome',
  'mostSevereConsequences',
  'type',
  'diseaseType',
  'diseaseClassName_MSH',
  'diseaseClassName_UMLS',
  'diseaseClassName_HPO',
  'diseaseClassName_DO',
  'pvalue_jaccard_genes',
  'pvalue_jaccard_variants',
  'sokal',
  'geneProteinClassNames',
  'proteinClassIDs',
  'genepLI',
  'PLI',
  'riskAllele',
  'or_or_beta',
  'pValue',
];

const totalRedirectNames = metricsRedirectNames.concat(
  sourcesRedirectNames,
  associationTypeRedirectNames,
  attributesRedirectNames,
);

export {
  metricsRedirectNames,
  sourcesRedirectNames,
  associationTypeRedirectNames,
  attributesRedirectNames,
  totalRedirectNames,
};

const listSourceAcademic = {
  CURATED: ['CURATED'],
};

// sources GDA and DDA
const listSourceGDAs_DDAs = {
  CURATED: [
    'CURATED',
    'CLINGEN',
    'CLINVAR',
    'CLINPGX',
    'GENCC',
    'MGD_HUMAN',
    'ORPHANET',
    'PSYGENET',
    'RGD_HUMAN',
    'UNIPROT',
  ],
  CLINICALTRIALS: ['CLINICALTRIALS'],
  INFERRED: [...INFERRED_SOURCE_API_IDS],
  MODELS: ['MODELS', 'MGD_MOUSE', 'RGD_RAT', 'TEXTMINING_MODELS'],
  TEXTMINING: ['TEXTMINING_HUMAN'],
  BIOBANK: ['BIOBANK', 'FINNGEN', 'UKBIOBANK'],
  ALL: ['ALL'],
};

// sources Variant and VDAs
const listSourceVariantAll_VDAs = {
  CURATED: ['CURATED', 'CLINGEN', 'CLINVAR', 'CLINPGX', 'GWASCAT', 'PHEWASCAT', 'UNIPROT'],
  TEXTMINING: ['TEXTMINING_HUMAN'],
  BIOBANK: ['BIOBANK', 'FINNGEN', 'UKBIOBANK'],
  ALL: ['ALL'],
};

// sources Disease
const listSourceDiseaseAll = {
  CURATED: [
    'CURATED',
    'CLINGEN',
    'CLINVAR',
    'CLINPGX',
    'GENCC',
    'MGD_HUMAN',
    'ORPHANET',
    'PSYGENET',
    'RGD_HUMAN',
    'UNIPROT',
  ],
  CLINICALTRIALS: ['CLINICALTRIALS'],
  BIOBANK: ['BIOBANK', 'FINNGEN', 'UKBIOBANK'],
  INFERRED: [...INFERRED_SOURCE_API_IDS],
  MODELS: ['MODELS', 'MGD_MOUSE', 'RGD_RAT', 'TEXTMINING_MODELS'],
  TEXTMINING: ['TEXTMINING_HUMAN'],
  ALL: ['ALL'],
};

// sources Gene
const listSourceGeneALL = {
  CURATED: [
    'CURATED',
    'CLINGEN',
    'CLINVAR',
    'CLINPGX',
    'GENCC',
    'MGD_HUMAN',
    'ORPHANET',
    'PSYGENET',
    'RGD_HUMAN',
    'UNIPROT',
  ],
  CLINICALTRIALS: ['CLINICALTRIALS'],
  INFERRED: [...INFERRED_SOURCE_API_IDS],
  MODELS: ['MODELS', 'MGD_MOUSE', 'RGD_RAT', 'TEXTMINING_MODELS'],
  TEXTMINING: ['TEXTMINING_HUMAN'],
  ALL: ['ALL'],
};

// sources CHEMICALS
const listSourceChemicalAll = {
  CURATED: ['CURATED', 'CLINPGX'],
  CLINICALTRIALS: ['CLINICALTRIALS'],
  ...(isRelease261Enabled ? { CHEMBL: ['CHEMBL'] } : {}),
  MODELS: ['MODELS', 'TEXTMINING_MODELS'],
  TEXTMINING: ['TEXTMINING_HUMAN'],
  ALL: ['ALL'],
};

export interface EvidenceParams {
  assocID: string;
  text: string;
  diseaseClassName_DO: string;
  diseaseClassName_HPO: string;
  diseaseClassName_MSH: string;
  diseaseClassName_UMLS: string;
  diseaseName: string;
  diseaseUMLSCUI: string;
  geneNcbiType: string;
  geneDescription: string;
  journal: string;
  source: string;
  sentenceHTML: string;
  html: string;
  geneSymbol: string;
  reference: string;
  reference_type: string;
  pmYear: string;
  chemIDs: string[];
  pmid: string;
}

const getEvidenceParamsToSend = (data: EvidenceParams[]) => {
  return data.map(item => {
    return {
      id: item.assocID,
      text: item.text,
      geneSymbol: item.geneSymbol,
      geneDescription: item.geneDescription,
      disease: item.diseaseName,
      referenceYear: item.pmYear,
      reference: item.reference,
      referenceType: item.reference_type,
    };
  });
};

const evidenceSummaryRequest = async (params: EvidenceParams[], dispatch) => {
  try {
    if (dispatch) dispatch({ type: 'SET_LOADING', payload: true });
    const url = `${process.env.REACT_APP_URL_CHAT_API}summarize`;
    const payload = getEvidenceParamsToSend(params);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.text();
    if (dispatch) dispatch({ type: 'SET_LOADING', payload: false });
    return result;
  } catch (e) {
    if (dispatch) dispatch({ type: 'SET_LOADING', payload: false });
    console.error(e);
    return null;
  }
};

export {
  listSourceGDAs_DDAs,
  listSourceVariantAll_VDAs,
  listSourceAcademic,
  listSourceDiseaseAll,
  listSourceGeneALL,
  listSourceChemicalAll,
  getEvidenceParamsToSend,
  evidenceSummaryRequest,
};
